import { MicrosoftService } from '@api';

import { POSTHOG_TRACKING_EVENTS, PosthogTrackingSource } from '../constants';
import { CaptureTrackingEvent } from './types';

export interface PromptRefinerTrackingArgs {
  source: PosthogTrackingSource;
  conversationId?: string;
  messageId?: string;
}

export const createAlfredEvents = (
  captureTrackingEvent: CaptureTrackingEvent
) => {
  const trackAlfredQuerySent = ({
    source,
    type,
    via,
    value,
    conversationId,
    messageId,
    isPromptRefined,
  }: {
    source: PosthogTrackingSource;
    type: string;
    via?: PosthogTrackingSource;
    value?: string;
    conversationId?: string;
    messageId?: string;
    isPromptRefined?: boolean;
  }) => {
    const properties = {
      source,
      type,
      ...(via && { via }),
      ...(value && { value }),
      ...(conversationId && { conversation_id: conversationId }),
      ...(messageId && { message_id: messageId }),
      ...(isPromptRefined !== undefined && {
        is_prompt_refined: isPromptRefined,
      }),
    };

    return captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.QUERY_SENT,
      properties,
    });
  };

  const buildPromptRefinerProperties = ({
    source,
    conversationId,
    messageId,
  }: PromptRefinerTrackingArgs) => ({
    source,
    ...(conversationId && { conversation_id: conversationId }),
    ...(messageId && { message_id: messageId }),
  });

  const trackPromptRefinerOpened = (args: PromptRefinerTrackingArgs) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.PROMPT_REFINER_OPENED,
      properties: buildPromptRefinerProperties(args),
    });

  const trackPromptRefinerClarificationAdded = (
    args: PromptRefinerTrackingArgs
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.PROMPT_REFINER_CLARIFICATION_ADDED,
      properties: buildPromptRefinerProperties(args),
    });

  const trackPromptRefinerSuggestionEdited = (
    args: PromptRefinerTrackingArgs
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.PROMPT_REFINER_SUGGESTION_EDITED,
      properties: buildPromptRefinerProperties(args),
    });

  const trackAlfredPythonView = (
    source: PosthogTrackingSource,
    type: 'expand' | 'copy'
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_PYTHON_VIEW,
      properties: { source, type },
    });

  const trackAlfredProcessView = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_PROCESS_VIEW,
      properties: { source },
    });

  const trackAlfredShare = (
    source: PosthogTrackingSource,
    type: 'create' | 'copy' | 'continue'
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_SHARE,
      properties: { source, type },
    });

  const trackAlfredPersonalize = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_PERSONALIZE,
      properties: { source },
    });

  const trackAlfredChatSearchOpen = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_CHATSEARCH_OPEN,
      properties: { source },
    });

  const trackAlfredChartAction = (
    source: PosthogTrackingSource,
    type: 'download' | 'expand'
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_CHART_ACTION,
      properties: { source, type },
    });

  const trackAlfredFlowOpen = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_FLOW_OPEN,
      properties: { source },
    });

  const trackAlfredPlanningView = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_PLANNING_VIEW,
      properties: { source },
    });

  const trackAlfredChatBookmark = (
    source: PosthogTrackingSource,
    type: 'bookmark' | 'unbookmark'
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_CHAT_BOOKMARK,
      properties: { source, type },
    });

  const trackAlfredDeepSelected = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_DEEP_SELECTED,
      properties: { source },
    });

  const trackAlfredWebSearchSelected = (
    source: PosthogTrackingSource,
    type: 'general' | 'trusted' | 'none'
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_WEB_SEARCH_SELECTED,
      properties: { source, type },
    });

  const trackAlfredPreviousConversationOpen = (
    source: PosthogTrackingSource,
    type?: 'shared'
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_PREVIOUS_CONVERSATION_OPEN,
      properties: { source, ...(type && { type }) },
    });

  const trackAlfredTrustedSourceAdded = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_TRUSTED_SOURCE_ADDED,
      properties: { source },
    });

  const trackAlfredTrustedSourceRemoved = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_TRUSTED_SOURCE_REMOVED,
      properties: { source },
    });

  const trackAlfredSourceOpen = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_SOURCE_OPEN,
      properties: { source },
    });

  const trackAlfredChatCopy = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_CHAT_COPY,
      properties: { source },
    });

  const trackAlfredFeedback = (
    source: PosthogTrackingSource,
    type: 'thumbs_up' | 'thumbs_down'
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_FEEDBACK,
      properties: { source, type },
    });

  const trackAlfredConversationPrint = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_CONVERSATION_PRINT,
      properties: { source },
    });

  const trackAlfredConversationRename = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_CONVERSATION_RENAME,
      properties: { source },
    });

  const trackAlfredConversationStarted = (
    source: PosthogTrackingSource | null
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_CONVERSATION_STARTED,
      properties: { source },
    });

  const trackAlfredOpened = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_OPENED,
      properties: { source },
    });

  const trackAlfredConnectedServiceFileOpen = (subtype: MicrosoftService) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ALFRED_CONNECTED_SERVICE_FILE_OPEN,
      properties: { source: null, subtype },
    });

  return {
    trackAlfredQuerySent,
    trackAlfredPythonView,
    trackAlfredProcessView,
    trackAlfredShare,
    trackAlfredPersonalize,
    trackAlfredChatSearchOpen,
    trackAlfredChartAction,
    trackAlfredFlowOpen,
    trackAlfredPlanningView,
    trackAlfredChatBookmark,
    trackAlfredDeepSelected,
    trackAlfredWebSearchSelected,
    trackAlfredPreviousConversationOpen,
    trackAlfredTrustedSourceAdded,
    trackAlfredTrustedSourceRemoved,
    trackAlfredSourceOpen,
    trackAlfredChatCopy,
    trackAlfredFeedback,
    trackAlfredConversationPrint,
    trackAlfredConversationRename,
    trackAlfredConversationStarted,
    trackAlfredOpened,
    trackAlfredConnectedServiceFileOpen,
    trackPromptRefinerOpened,
    trackPromptRefinerClarificationAdded,
    trackPromptRefinerSuggestionEdited,
  };
};
